import React, { useRef, useState } from "react";
import {
  BriefcaseIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  VideoCameraIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  SparklesIcon,
  ShieldCheckIcon,
  FireIcon,
} from "@heroicons/react/24/solid";
import data from "../data";
import VideoModal from "../components/VideoModal";
import MachineSalesForm from "../components/contact/MachineSalesForm";

const Machine = () => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const machineSectionRef = useRef(null);

  const openVideoModal = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setVideoModalOpen(true);
  };

  // Scroll to "Get Your Own Machine" section
  const scrollToMachineSection = () => {
    if (machineSectionRef.current) {
      machineSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <div className="relative w-full h-[70vh] flex items-center justify-center bg-black text-white px-6 md:px-12">
        <video
          src={data.machine.heroVideo}
          autoPlay
          loop
          muted
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="relative text-center z-10 max-w-4xl">
          <h1 className="text-5xl font-bold">{data.machine.title}</h1>
          <p className="text-lg md:text-2xl mt-6">{data.machine.description}</p>
          <div className="mt-8 flex gap-6 justify-center">
            <button
              onClick={scrollToMachineSection}
              className="bg-primary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out  hover:font-bold "
            >
              Get Your Own Machine
            </button>
            <button
              onClick={() => openVideoModal(data.machine.demoVideo)}
              className="bg-secondary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out  hover:font-bold "
            >
              {" "}
              Watch Demo
            </button>
          </div>
        </div>
      </div>

      {/* Divider Section */}
      <div className="bg-gray-100 mt-5 py-12 text-center">
        <div className="flex flex-col md:flex-row justify-center gap-8 max-w-6xl mx-auto">
          {data.machine.divider.map((item, index) => (
            <div key={index}>
              <h3 className="text-xl font-semibold text-primary">
                {item.title}
              </h3>
              <p className="text-gray-600 mt-2">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="w-full px-4 md:px-8 lg:px-16">
        {data.machine.features.map((feature, index) => (
          <div
            key={index}
            className={`w-full flex flex-col pt-5 gap-5 ${
              index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
            }`}
          >
            {/* Image Section */}
            <div className="w-full md:w-1/2">
              <img
                src={feature.image}
                alt={feature.title}
                className="w-full h-auto max-h-[800px] object-cover"
              />
            </div>

            {/* Text Section */}
            <div className="w-full md:w-1/2 flex items-center">
              <div className="px-6 md:px-8 py-6 md:py-12 md:mx-24">
                <h3 className="text-3xl md:text-4xl font-semibold text-primary">
                  {feature.title}
                </h3>
                <p className="text-base md:text-lg text-gray-600 mt-4">
                  {feature.description}
                </p>
                {feature.link && (
                  <a
                    href={feature.link}
                    className="text-primary underline mt-4 inline-block"
                  >
                    {feature.linkText}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Environmentally Friendly Section */}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 xl:px-24 py-24">
        <h2 className="text-5xl font-bold text-center text-primary">
          {data.machine.ecoFriendly.title}
        </h2>
        <p className="text-lg text-gray-700 text-center mt-4 max-w-2xl mx-auto">
          {data.machine.ecoFriendly.description}
        </p>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-center">
          {data.machine.ecoFriendly.features.map((feature, index) => {
            const icons = [
              GlobeAltIcon,
              SparklesIcon,
              CheckCircleIcon,
              ShieldCheckIcon,
              WrenchScrewdriverIcon,
              FireIcon,
            ];
            const IconComponent = icons[index];
            return (
              <div key={index} className="flex flex-col items-center">
                <IconComponent className="text-secondary w-12 h-12 mb-4" />
                <h3 className="text-xl font-semibold text-primary">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mt-2">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transition Section */}
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12">
        {data.machine.transitionImages.map((img, index) => (
          <img
            key={index}
            src={img.image}
            alt={img.alt}
            className={`w-full h-[500px] object-cover ${
              index === 2 ? "sm:col-span-2" : ""
            }`}
          />
        ))}
      </div>

      {/* Easy Setup Section */}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 xl:px-24 py-24">
        <h2 className="text-5xl text-primary font-bold text-center">
          {data.machine.setup.title}
        </h2>
        <p className="text-lg text-gray-700 text-center mt-4 max-w-2xl mx-auto">
          {data.machine.setup.description}
        </p>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {data.machine.setup.details.map((detail, index) => {
            const icons = [
              BriefcaseIcon,
              WrenchScrewdriverIcon,
              TruckIcon,
              VideoCameraIcon,
            ];
            const IconComponent = icons[index];
            return (
              <div key={index} className="flex flex-col items-center">
                <IconComponent className="text-secondary w-12 h-12 mb-4" />
                <h3 className="text-xl font-semibold text-primary">
                  {detail.title}
                </h3>

                <p className="text-gray-600 mt-2">
                  {detail.title === "Setup Video" ? (
                    <>
                      See how simple setup is—watch{" "}
                      <button
                        className="text-primary underline hover:text-secondary"
                        onClick={() =>
                          openVideoModal(data.machine.setup.setupVideo)
                        }
                      >
                        here
                      </button>
                      .
                    </>
                  ) : (
                    detail.description
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* "Get Your Own Machine" Section */}
      <div ref={machineSectionRef} className="bg-gray-100 py-24">
        <div className="container mx-auto flex flex-col md:flex-row items-center gap-12">
          {/* Image Section */}
          <div className="w-full md:w-1/2">
            <img
              src={data.machine.pricing.image}
              alt="WallPen in Use"
              className="w-full h-auto max-h-[700px] object-contain"
            />
          </div>

          {/* Form Section */}
          <div className="w-full md:w-1/2 text-center md:text-left lg:max-h-[700px] overflow-auto">
            <h2 className="text-5xl font-bold text-primary">
              {data.machine.pricing.title}
            </h2>
            <p className="text-lg text-gray-700 mt-4 max-w-lg">
              {data.machine.pricing.description}
            </p>
            <p className="text-2xl font-semibold text-primary mt-4">
              {data.machine.pricing.price}
            </p>

            {/* Embed MachineSalesForm */}
            <div className="mt-6">
              <MachineSalesForm />
            </div>
          </div>
        </div>
      </div>

      <VideoModal
        videoUrl={currentVideoUrl}
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
      />
    </div>
  );
};

export default Machine;
